import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { useLocation as useSavedLocation } from "@/context/LocationContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Banknote, CreditCard, MapPin, ChevronLeft, ChevronRight, Home, Briefcase, Pin, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const SAVED_KEY = "jayek_saved_addresses";

function loadSavedAddresses() {
  try { return JSON.parse(localStorage.getItem(SAVED_KEY) || "[]"); } catch { return []; }
}

function labelIcon(label) {
  const l = (label || "").toLowerCase();
  if (l.includes("home") || l.includes("منزل") || l.includes("بيت")) return Home;
  if (l.includes("work") || l.includes("عمل") || l.includes("مكتب")) return Briefcase;
  return Pin;
}

export default function Checkout() {
  const { t, lang, isRTL } = useLang();
  const { cart, subtotal, clearCart } = useCart();
  const { user } = useAuth();
  const { location } = useSavedLocation();
  const navigate = useNavigate();
  const Chevron = isRTL ? ChevronLeft : ChevronRight;

  const [submitting, setSubmitting] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState(loadSavedAddresses);
  const [selectedAddrId, setSelectedAddrId] = useState(() => loadSavedAddresses()[0]?.id || null);
  const [showProducts, setShowProducts] = useState(false);
  const [notes, setNotes] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash"); // cash | card | apple_pay

  const selectedAddr = useMemo(
    () => savedAddresses.find((a) => a.id === selectedAddrId) || null,
    [savedAddresses, selectedAddrId]
  );

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-6 py-20 text-center">
        <h1 className="font-display text-3xl font-black text-[#1A1A1A]">{t("loginToContinue")}</h1>
        <Button
          onClick={() => navigate("/login", { state: { from: "/checkout" } })}
          className="mt-8 rounded-full bg-[#E05D3A] hover:bg-[#C24B2C] text-white px-8 py-6"
          data-testid="checkout-signin-btn"
        >
          {t("signInCta")}
        </Button>
      </div>
    );
  }

  if (cart.items.length === 0) {
    navigate("/cart");
    return null;
  }

  const deliveryFee = cart.restaurant?.delivery_fee || 10;
  const total = subtotal + deliveryFee;

  // Build the address object from the picked saved address OR from current map location
  const buildAddressPayload = () => {
    if (selectedAddr) {
      return {
        label: selectedAddr.label || (lang === "ar" ? "المنزل" : "Home"),
        street: [selectedAddr.street, selectedAddr.building, selectedAddr.floor && `${lang === "ar" ? "طابق" : "Floor"} ${selectedAddr.floor}`, selectedAddr.unit && `${lang === "ar" ? "شقة" : "Unit"} ${selectedAddr.unit}`].filter(Boolean).join("، "),
        city: lang === "ar" ? selectedAddr.city_ar : selectedAddr.city_en,
        notes: [selectedAddr.notes, notes].filter(Boolean).join(" · "),
        lat: selectedAddr.lat,
        lng: selectedAddr.lng,
      };
    }
    // Fallback to map-picked location
    return {
      label: lang === "ar" ? "موقعي" : "My location",
      street: location.street || `${lang === "ar" ? location.district_ar : location.district_en}`,
      city: lang === "ar" ? location.city_ar : location.city_en,
      notes,
      lat: location.lat,
      lng: location.lng,
    };
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const { data: order } = await api.post("/orders", {
        restaurant_id: cart.restaurant.id,
        items: cart.items.map((i) => ({
          menu_item_id: i.id,
          name_en: i.name_en,
          name_ar: i.name_ar,
          price: i.price,
          quantity: i.quantity,
        })),
        address: buildAddressPayload(),
        payment_method: paymentMethod === "apple_pay" ? "card" : paymentMethod,
        notes,
      });

      if (paymentMethod === "card" || paymentMethod === "apple_pay") {
        const { data } = await api.post("/checkout/session", {
          order_id: order.order_id,
          origin_url: window.location.origin,
        });
        window.location.href = data.url;
      } else {
        clearCart();
        toast.success(lang === "ar" ? "تم تأكيد الطلب!" : "Order placed!");
        navigate(`/order/${order.order_id}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto pb-[calc(env(safe-area-inset-bottom)+11rem)] md:pb-10">
      {/* Page sub-header (scrolls with content; global Jayek header stays fixed) */}
      <div className="bg-white border-b border-[#EAEAEA]">
        <div className="px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full hover:bg-[#F5F5F5] flex items-center justify-center"
            data-testid="checkout-back"
          >
            <Chevron className="w-5 h-5" />
          </button>
          <h1 className="font-bold text-lg text-[#1A1A1A]">{lang === "ar" ? "إتمام الطلب" : "Checkout"}</h1>
        </div>
      </div>

      <div className="px-4 pt-4 space-y-4">
        {/* Products section (collapsible) */}
        <section className="bg-white rounded-2xl border border-[#EAEAEA] p-4">
          <button
            type="button"
            onClick={() => setShowProducts((s) => !s)}
            className="w-full flex items-center justify-between"
            data-testid="toggle-products"
          >
            <span className="font-bold text-[15px] text-[#1A1A1A]">
              {lang === "ar" ? `المنتجات (${cart.items.length})` : `Products (${cart.items.length})`}
            </span>
            <span className="flex items-center gap-1 text-[13px] text-[#E05D3A] font-semibold">
              {showProducts ? (lang === "ar" ? "إخفاء" : "Hide") : (lang === "ar" ? "أظهر" : "Show")}
              <ChevronDown className={`w-4 h-4 transition-transform ${showProducts ? "rotate-180" : ""}`} />
            </span>
          </button>
          {showProducts && (
            <ul className="mt-4 space-y-3">
              {cart.items.map((i) => (
                <li key={i.id} className="flex items-center gap-3 text-sm" data-testid={`cart-line-${i.id}`}>
                  <img src={i.image} alt="" className="w-12 h-12 rounded-xl object-cover bg-[#F0F0F0]" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#1A1A1A] truncate">{lang === "ar" ? i.name_ar : i.name_en}</p>
                    <p className="text-xs text-[#6B6B6B]">{i.quantity} × {i.price.toFixed(2)} SAR</p>
                  </div>
                  <p className="font-bold text-[#1A1A1A] whitespace-nowrap">{(i.quantity * i.price).toFixed(2)}</p>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Delivery address — map only */}
        <section className="bg-white rounded-2xl border border-[#EAEAEA] p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#E05D3A]" />
              <h3 className="font-bold text-[15px] text-[#1A1A1A]">{lang === "ar" ? "التوصيل إلى" : "Delivery to"}</h3>
            </div>
            <button
              type="button"
              onClick={() => navigate("/location")}
              className="text-[13px] font-semibold text-[#E05D3A] hover:underline"
              data-testid="change-location-btn"
            >
              {lang === "ar" ? "تغيير" : "Change"}
            </button>
          </div>

          {/* Saved addresses list — click to pick; none = use current map location */}
          {savedAddresses.length > 0 ? (
            <div className="space-y-2">
              {savedAddresses.map((a) => {
                const IconComp = labelIcon(a.label);
                const active = selectedAddrId === a.id;
                return (
                  <button
                    type="button"
                    key={a.id}
                    onClick={() => setSelectedAddrId(a.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border text-start transition ${active ? "border-[#E05D3A] bg-[#FFF1EA]" : "border-[#EAEAEA] hover:border-[#E05D3A]/40"}`}
                    data-testid={`pick-saved-${a.id}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${active ? "bg-[#E05D3A] text-white" : "bg-[#F5F5F5] text-[#E05D3A]"}`}>
                      <IconComp className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[14px] text-[#1A1A1A] truncate">{a.label || a.street}</p>
                      <p className="text-[12px] text-[#6B6B6B] truncate">
                        {[a.street, lang === "ar" ? a.city_ar : a.city_en].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                    {active && <div className="w-5 h-5 rounded-full bg-[#E05D3A] flex-shrink-0" />}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => { setSelectedAddrId(null); navigate("/location"); }}
                className="w-full mt-2 p-3 rounded-xl border border-dashed border-[#E05D3A] text-[#E05D3A] font-semibold text-[13px] hover:bg-[#FFF1EA]"
                data-testid="add-address-btn"
              >
                + {lang === "ar" ? "اختر عنوان جديد من الخريطة" : "Pick a new address on the map"}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => navigate("/location")}
              className="w-full flex items-center gap-3 p-3 rounded-xl border border-dashed border-[#E05D3A] hover:bg-[#FFF1EA] text-start"
              data-testid="pick-location-btn"
            >
              <div className="w-10 h-10 rounded-full bg-[#FFF1EA] flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-[#E05D3A]" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-[14px] text-[#1A1A1A]">
                  {lang === "ar" ? location.city_ar : location.city_en} · {lang === "ar" ? location.district_ar : location.district_en}
                </p>
                <p className="text-[12px] text-[#E05D3A] font-semibold mt-0.5">
                  {lang === "ar" ? "اضغط لتحديد الموقع من الخريطة" : "Tap to pick on the map"}
                </p>
              </div>
            </button>
          )}

          {/* Optional driver notes */}
          <div className="mt-4">
            <label className="block text-[13px] font-semibold text-[#1A1A1A] mb-2">
              {lang === "ar" ? "ملاحظات للسائق (اختياري)" : "Notes for the driver (optional)"}
            </label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder={lang === "ar" ? "مثال، اتصل عند الوصول" : "e.g., call on arrival"}
              className="rounded-xl"
              data-testid="driver-notes"
            />
          </div>
        </section>

        {/* Payment method */}
        <section className="bg-white rounded-2xl border border-[#EAEAEA] p-4">
          <h3 className="font-bold text-[15px] text-[#1A1A1A] mb-3">{lang === "ar" ? "طريقة الدفع" : "Payment method"}</h3>
          <div className="space-y-2">
            <PaymentOption
              active={paymentMethod === "cash"}
              onClick={() => setPaymentMethod("cash")}
              testId="payment-cash"
              icon={<Banknote className="w-5 h-5 text-[#00A85A]" />}
              title={lang === "ar" ? "الدفع عند الاستلام" : "Cash on delivery"}
              subtitle={lang === "ar" ? "ادفع نقداً عند وصول الطلب" : "Pay cash when your order arrives"}
            />
            <PaymentOption
              active={paymentMethod === "card"}
              onClick={() => setPaymentMethod("card")}
              testId="payment-card"
              icon={<CreditCard className="w-5 h-5 text-[#E05D3A]" />}
              title={lang === "ar" ? "بطاقة ائتمان / خصم" : "Credit / Debit card"}
              subtitle="Visa · Mastercard · Mada"
            />
            <PaymentOption
              active={paymentMethod === "apple_pay"}
              onClick={() => setPaymentMethod("apple_pay")}
              testId="payment-applepay"
              icon={<ApplePayLogo />}
              title="Apple Pay"
              subtitle={lang === "ar" ? "دفع سريع وآمن عبر Apple Pay" : "Fast & secure via Apple Pay"}
            />
          </div>
        </section>

        {/* Totals */}
        <section className="bg-white rounded-2xl border border-[#EAEAEA] p-4">
          <div className="space-y-2 text-sm text-[#1A1A1A]">
            <div className="flex justify-between"><span className="text-[#6B6B6B]">{t("subtotal")}</span><span className="font-semibold">{subtotal.toFixed(2)} SAR</span></div>
            <div className="flex justify-between"><span className="text-[#6B6B6B]">{t("deliveryFee")}</span><span className="font-semibold">{deliveryFee.toFixed(2)} SAR</span></div>
            <div className="flex justify-between text-[16px] font-bold pt-3 border-t border-[#EAEAEA] mt-2">
              <span>{lang === "ar" ? "المجموع الكلي" : "Total"}</span>
              <span>{total.toFixed(2)} SAR</span>
            </div>
          </div>
        </section>
      </div>

      {/* Submit button — sits clearly above the bottom nav (and its safe-area) */}
      <div
        className="fixed inset-x-0 md:static md:mt-6 bg-white md:bg-transparent border-t md:border-0 border-[#EAEAEA] p-4 z-40 md:z-auto md:px-4"
        style={{ bottom: "calc(env(safe-area-inset-bottom) + 4rem)" }}
      >
        <div className="max-w-3xl mx-auto">
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full rounded-full bg-[#E05D3A] hover:bg-[#C24B2C] text-white py-6 font-bold text-[15px]"
            data-testid="place-order"
          >
            {submitting
              ? t("processing")
              : `${lang === "ar" ? "إرسال الطلب" : "Place order"} · ${total.toFixed(2)} SAR`}
          </Button>
        </div>
      </div>
    </div>
  );
}

function PaymentOption({ active, onClick, icon, title, subtitle, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-3 p-3 rounded-xl border text-start transition ${active ? "border-[#E05D3A] bg-[#FFF1EA]" : "border-[#EAEAEA] hover:border-[#E05D3A]/40"}`}
      data-testid={testId}
    >
      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${active ? "border-[#E05D3A]" : "border-[#CCCCCC]"}`}>
        {active && <div className="w-2.5 h-2.5 rounded-full bg-[#E05D3A]" />}
      </div>
      <div className="w-10 h-10 rounded-xl bg-[#F5F5F5] flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-[14px] text-[#1A1A1A]">{title}</p>
        <p className="text-[12px] text-[#6B6B6B] truncate">{subtitle}</p>
      </div>
    </button>
  );
}

function ApplePayLogo() {
  return (
    <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-black text-white leading-none">
      <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor">
        <path d="M17.05 12.04c-.03-2.66 2.17-3.94 2.27-4-1.24-1.81-3.17-2.06-3.85-2.08-1.64-.17-3.2.97-4.04.97-.85 0-2.13-.95-3.5-.92-1.8.03-3.46 1.05-4.39 2.66-1.87 3.24-.48 8.02 1.35 10.64.89 1.29 1.95 2.73 3.34 2.67 1.34-.05 1.85-.87 3.48-.87 1.62 0 2.08.87 3.5.84 1.45-.03 2.37-1.31 3.26-2.6 1.03-1.49 1.45-2.95 1.47-3.02-.03-.02-2.81-1.08-2.84-4.29zM14.4 4.2c.74-.89 1.24-2.14 1.1-3.38-1.07.04-2.36.71-3.12 1.6-.69.79-1.29 2.06-1.13 3.28 1.19.09 2.41-.6 3.15-1.5z" />
      </svg>
      <span style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif" }} className="text-[11px] font-semibold tracking-tight">Pay</span>
    </div>
  );
}
